import type { ReactNode } from 'react';
import React, { createContext, useContext, useMemo, useState } from 'react';
import { Modal } from '@wix/design-system';

type ModalController = {
  open: (requestedModalContent: ReactNode) => void;
  close: () => void;
};

type ModalProviderProps = {
  children: ReactNode;
};

const ModalContext = createContext<ModalController | undefined>(undefined);

export function ModalProvider(props: ModalProviderProps) {
  const [modalContent, setModalContent] = useState<ReactNode | null>(null);
  const isOpen = modalContent !== null;

  const open = (requestedModalContent: ReactNode) => {
    setModalContent(requestedModalContent);
  };

  const close = () => {
    setModalContent(null);
  };

  const modalControls = useMemo<ModalController>(
    () => ({
      open,
      close,
    }),
    [],
  );

  return (
    <ModalContext.Provider value={modalControls}>
      {props.children}
      <Modal isOpen={isOpen}>{modalContent}</Modal>
    </ModalContext.Provider>
  );
}

export function useModal() {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal should be used inside a ModalProvider');
  }

  return context;
}
